// src/features/leases/EditLeaseModal.jsx
//
// LSE-UNIFIED-EDIT-UX-PHASE-2 Sub-phase 1b — unified edit modal for lease
// data. Renders inside LeaseSummaryTab when canWrite && lease is loaded.
//
// Design (§5.21):
//   Header  "Edit lease data · {lease.id}"
//   Body    6 accordion sections — 2 open by default, 4 collapsed
//   Footer  Cancel + Save changes
//
// Draft state is local to this component (initialized on every open from
// the current lease prop). On Save: PATCH changed fields → delete marked
// year-cost rows → add new year-cost rows. Any failure keeps the modal open
// and surfaces a banner. Dirty-form confirm guards accidental dismissal.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "../../components/Modal.jsx";
import { T } from "../../app/tokens.js";
import { DateInput } from "../../components/DateInput.jsx";
import { updateLease, updateLeaseArea, addYearCost, updateYearCost, deleteYearCost, deleteDraftLease, voidLease } from "../../data/leasesClient.js";
import { LEASE_TYPE_OPTIONS } from "./leaseTypes.js";
import { LEASE_STATUS_OPTIONS, LEASE_BUCKET_TO_DB_STATUS } from "./leaseStatusLabels.js";
import { LeaseAreasTable } from "./components/LeaseAreasTable.jsx";
import { LeaseAreaEditor, parseAreaFootage } from "./components/LeaseAreaEditor.jsx";
import { parseRentRateDrafts, rateDraft } from "./components/LeaseAreaRentEditor.jsx";
import { PaneHeading } from "../../components/PaneHeading.jsx";
import { log } from "../../utils/logger.js";
import { statusFor } from "./pages/components/LeasePortfolioFilters.jsx";
import { MonthlyRentBasis } from "./components/MonthlyRentBasis.jsx";
import { EVEN_INSTALLMENT_NOTE } from "../../../shared/leaseMonthlyDisplay.js";
import { showToast } from "../../components/toastService.jsx";

// Maps the persisted lease status plus its legacy DB status/type fallback to
// the display bucket used by LEASE_STATUS_OPTIONS. An explicit leaseStatus is
// authoritative; otherwise this preserves the derived behavior for older rows.
function dbStatusToEditBucket(status, type, leaseStatus = null) {
  return statusFor({ status, type, leaseStatus });
}

const TITLE_ID = "edit-lease-modal-title";

// ─── Field → accordion section mapping ───────────────────────────────────────
// Maps each editable field name to the `expanded` key of the section that
// contains it. Used by the `initialField` prop to pre-expand and focus the
// correct section when the modal is opened from a "+ add" affordance.
// Sections without a key here ("leaseDetails", "financial") are always visible.
const FIELD_TO_SECTION = {
  areas: "areas",
  // Lease details (always expanded — scroll + focus without toggling)
  status: "leaseDetails", type: "leaseDetails", size: "leaseDetails",
  vendor: "leaseDetails", lessee: "leaseDetails",
  startDate: "leaseDetails", endDate: "leaseDetails",
  moveInDate: "leaseDetails", moveOutDate: "leaseDetails",
  // Financial terms (always expanded)
  totalCost: "financial", currency: "financial",
  rentBasis: "financial",
  freeRentMonths: "financial", escalationType: "financial",
  opexStructure: "financial",
  // Critical dates (collapsible — expanded on demand)
  breakDate: "criticalDates", renewalNoticeMonths: "criticalDates",
  // Options (collapsible)
  renewalCount: "options", rofrRofoNotes: "options",
  expansionOptionNotes: "options", securityDepositAmount: "options",
  // Restrictions & concessions (collapsible)
  permittedUse: "restrictions", subletConsentRequired: "restrictions",
  sqftMeasurementStandard: "restrictions", subletRecaptureRight: "restrictions",
  restorationObligations: "restrictions", subletProfitSharePercent: "restrictions",
  baseYear: "restrictions", baseYearOpex: "restrictions",
  baseYearTax: "restrictions", opexCapPercent: "restrictions",
  noticeAddress: "restrictions",
  // AI summary (collapsible)
  aiSummary: "aiSummary",
};

// Sections that are always visible (non-collapsible) — no expand needed.
const ALWAYS_EXPANDED_SECTIONS = new Set(["leaseDetails", "financial"]);

// ─── Style constants ──────────────────────────────────────────────────────────

const FIELD_ROW_STYLE = {
  display: "grid",
  gridTemplateColumns: "150px 1fr",
  alignItems: "start",
  gap: 10,
  padding: "8px 0",
  borderBottom: `1px solid ${T.border}`,
};

const LABEL_STYLE = {
  fontSize: T.fsSm,
  color: T.mid,
  fontWeight: T.fwSemibold,
  paddingTop: 6,
};

const INPUT_BASE = {
  fontSize: T.fsBase,
  padding: "5px 8px",
  border: `1px solid ${T.borderDark}`,
  borderRadius: 4,
  width: "100%",
  fontFamily: "inherit",
  background: T.white,
  color: T.dark,
  boxSizing: "border-box",
};

// ─── Comparison helper (handles ISO date normalisation) ───────────────────────

function valuesEqual(a, b) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (typeof a === "boolean" || typeof b === "boolean") return Boolean(a) === Boolean(b);
  const sa = String(a), sb = String(b);
  const looksDate = (s) => /^\d{4}-\d{2}-\d{2}/.test(s);
  if (looksDate(sa) || looksDate(sb)) return sa.slice(0, 10) === sb.slice(0, 10);
  if (!isNaN(Number(sa)) && !isNaN(Number(sb))) return Number(sa) === Number(sb);
  return sa === sb;
}

// ─── Small presentational helpers ────────────────────────────────────────────

function SectionBar({ title, collapsible = false, expanded, onToggle }) {
  const content = (
    <PaneHeading
      style={{
        marginBottom: 4,
        padding: "8px 0",
        borderBottom: `2px solid ${T.border}`,
        cursor: collapsible ? "pointer" : "default",
        userSelect: "none",
      }}
      labelStyle={{ display: "flex", alignItems: "center", flex: 1 }}
    >
      {title}
      {collapsible && (
        <span style={{ marginLeft: "auto", fontSize: T.fsXs, color: T.mid }}>
          {expanded ? "▲" : "▼"}
        </span>
      )}
    </PaneHeading>
  );
  return collapsible
    ? <button type="button" onClick={onToggle} style={{ all: "unset", display: "block", width: "100%", cursor: "pointer" }}>{content}</button>
    : content;
}

function FRow({ label, children }) {
  return (
    <div style={FIELD_ROW_STYLE}>
      <span style={LABEL_STYLE}>{label}</span>
      <div>{children}</div>
    </div>
  );
}

function RORow({ label, value }) {
  return (
    <div style={FIELD_ROW_STYLE}>
      <span style={LABEL_STYLE}>{label}</span>
      <span style={{ fontSize: T.fsBase, color: value != null ? T.dark : T.light, fontWeight: value != null ? 600 : 400, paddingTop: 5 }}>
        {value ?? "—"}
      </span>
    </div>
  );
}

function TxtInput({ value, onChange, placeholder, disabled }) {
  return (
    <input
      type="text"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      placeholder={placeholder ?? ""}
      disabled={disabled}
      style={{ ...INPUT_BASE, opacity: disabled ? 0.6 : 1 }}
    />
  );
}

function NumInput({ value, onChange, disabled, min }) {
  return (
    <input
      type="number"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      disabled={disabled}
      min={min}
      style={{ ...INPUT_BASE, width: "100%", opacity: disabled ? 0.6 : 1 }}
    />
  );
}

function DtInput({ value, onChange, disabled }) {
  function toInputVal(v) {
    if (!v) return "";
    const s = String(v);
    return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : "";
  }
  return (
    <DateInput
      value={toInputVal(value)}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      style={{ ...INPUT_BASE, opacity: disabled ? 0.6 : 1 }}
    />
  );
}

function SelInput({ value, onChange, options, disabled, ariaLabel }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      aria-label={ariaLabel}
      style={{ ...INPUT_BASE, opacity: disabled ? 0.6 : 1 }}
    >
      <option value=""> - Select - </option>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const l = typeof o === "string" ? o : o.label;
        return <option key={v} value={v}>{l}</option>;
      })}
    </select>
  );
}

// ─── Per-year costs table ─────────────────────────────────────────────────────
// EDIT-LEASE-MODAL-AUDIT: existing rows now show editable number inputs for
// rent/opex/capex. Pending (newly added) rows stay read-only until POSTed.
// The "Rent" column header and add-form label have been renamed to the
// canonical field label. Total is always recomputed from the effective values.

// EDIT-LEASE-MODAL-PHASE2B: added Abatement + Net columns.
// Column order: Year | Contractual | Abatement | OpEx | Capex | Net | Total | (delete)
const YC_GRID = "80px 1fr 1fr 1fr 1fr 1fr 1fr 32px";
const YC_HDR  = { fontSize: T.fsXxs, fontWeight: T.fwSemibold, color: T.mid, textTransform: "uppercase", letterSpacing: "0.04em" };
const YC_RENT_LABEL = "Annual base rent (contractual)";

const YC_NUM_INPUT = {
  ...({} /* inline — inherits INPUT_BASE via style spread */),
  fontSize: T.fsSm,
  padding: "3px 5px",
  border: `1px solid ${T.borderDark}`,
  borderRadius: 3,
  width: "100%",
  fontFamily: "inherit",
  background: T.white,
  color: T.dark,
  boxSizing: "border-box",
};

function YearCostsTable({
  rows, pendingDelIds, newRowDraft,
  onDeleteExisting, onAddRow, onNewRowChange,
  ycEdits, onYcEdit,
  submitting,
}) {
  const visible = rows.filter((r) => !pendingDelIds.has(r.id));
  return (
    <div>
      {rows.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {/* Header row */}
          {visible.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: YC_GRID, gap: 6, padding: "4px 0", borderBottom: `1px solid ${T.border}` }}>
              {["Year", "Contractual", "Abatement", "OpEx", "Capex", "Net", "Total", ""].map((h) => (
                <span key={h} style={YC_HDR} title={h}>{h}</span>
              ))}
            </div>
          )}
          {visible.map((r) => {
            const eid = String(r.id);
            const ed  = ycEdits.get(eid);
            // Effective values: use the in-progress edit if present, else stored value.
            const effRent      = ed && "rent"      in ed ? ed.rent      : r.rent;
            const effAbatement = ed && "abatement" in ed ? ed.abatement : (r.abatement ?? 0);
            const effOpex      = ed && "opex"      in ed ? ed.opex      : r.opex;
            const effCapex     = ed && "capex"     in ed ? ed.capex     : r.capex;
            // Net = contractual − abatement (display-only, not sent to server as a field)
            const effNet   = (Number(effRent ?? 0) - Number(effAbatement ?? 0));
            // total_cost stays rent + opex + capex (abatement is NOT additive)
            const effTotal = (Number(effRent ?? 0) + Number(effOpex ?? 0) + Number(effCapex ?? 0)) || null;
            // Flag rows where rent is non-zero but OpEx appears missing or zero —
            // a common data gap from the original import that editors should review.
            const opexMissing = !r._pending && Number(effRent ?? 0) > 0 && (effOpex == null || Number(effOpex) === 0);

            return (
              <div
                key={r.id}
                style={{
                  display: "grid", gridTemplateColumns: YC_GRID, gap: 6,
                  padding: "5px 0", borderBottom: `1px solid ${T.border}`,
                  alignItems: "center", fontSize: T.fsSm,
                  opacity: r._pending ? 0.6 : 1,
                  background: ed ? T.ycEditRowBg : undefined,
                }}
              >
                <span style={{ fontWeight: T.fwSemibold }}>
                  {r.year}
                  {r._pending && <span style={{ fontSize: T.fsXxs, color: T.mid, marginLeft: 4, fontWeight: T.fwRegular }}>pending</span>}
                </span>

                {/* Contractual (rent) — editable for persisted rows, read-only for pending */}
                {r._pending
                  ? <span>{r.rent != null ? Number(r.rent).toLocaleString() : "—"}</span>
                  : <input
                      type="number"
                      aria-label={`Rent ${r.year}`}
                      value={effRent ?? ""}
                      disabled={submitting}
                      onChange={(e) => onYcEdit(eid, "rent", e.target.value === "" ? null : Number(e.target.value))}
                      style={YC_NUM_INPUT}
                    />
                }

                {/* Abatement — editable for persisted rows, read-only for pending */}
                {r._pending
                  ? <span>{effAbatement ? Number(effAbatement).toLocaleString() : "—"}</span>
                  : <input
                      type="number"
                      aria-label={`Abatement ${r.year}`}
                      value={effAbatement ?? ""}
                      disabled={submitting}
                      min={0}
                      onChange={(e) => onYcEdit(eid, "abatement", e.target.value === "" ? 0 : Number(e.target.value))}
                      style={YC_NUM_INPUT}
                    />
                }

                {/* OpEx — editable; amber indicator when rent is non-zero but opex is missing */}
                {r._pending
                  ? <span>{r.opex != null ? Number(r.opex).toLocaleString() : "—"}</span>
                  : (
                    <div>
                      <input
                        type="number"
                        aria-label={`OpEx ${r.year}`}
                        value={effOpex ?? ""}
                        disabled={submitting}
                        onChange={(e) => onYcEdit(eid, "opex", e.target.value === "" ? null : Number(e.target.value))}
                        style={{ ...YC_NUM_INPUT, ...(opexMissing ? { border: `1px solid ${T.amber}` } : {}) }}
                      />
                      {opexMissing && (
                        <span
                          data-testid={`opex-missing-${r.year}`}
                          title="OpEx is $0 while rent is non-zero - click to enter the correct value."
                          style={{ display: "block", fontSize: T.fsXxs, color: T.amberDark, marginTop: 2, lineHeight: 1.2 }}
                        >
                          ⚠ Missing?
                        </span>
                      )}
                    </div>
                  )
                }

                {/* Capex — editable */}
                {r._pending
                  ? <span>{r.capex != null ? Number(r.capex).toLocaleString() : "—"}</span>
                  : <input
                      type="number"
                      aria-label={`Capex ${r.year}`}
                      value={effCapex ?? ""}
                      disabled={submitting}
                      onChange={(e) => onYcEdit(eid, "capex", e.target.value === "" ? null : Number(e.target.value))}
                      style={YC_NUM_INPUT}
                    />
                }

                {/* Net = Contractual − Abatement — always read-only */}
                <span
                  aria-label={`Net ${r.year}`}
                  style={{ color: ed ? T.blue : T.mid, fontStyle: "italic" }}
                >
                  {Number(effNet).toLocaleString()}
                </span>

                {/* Total — always read-only, recomputed from effective values */}
                <span style={{ color: ed ? T.blue : undefined }}>
                  {effTotal != null ? Number(effTotal).toLocaleString() : "—"}
                </span>

                {r._pending
                  ? <span />
                  : <button
                      type="button"
                      onClick={() => onDeleteExisting(r.id)}
                      disabled={submitting}
                      aria-label={`Remove ${r.year}`}
                      style={{ background: "none", border: "none", color: T.red, cursor: submitting ? "not-allowed" : "pointer", fontSize: T.fsMd5, padding: 2, lineHeight: 1 }}
                    >×</button>
                }
              </div>
            );
          })}
          {/* Warning when rows are pending deletion */}
          {pendingDelIds.size > 0 && (
            <p style={{ fontSize: T.fsXs, color: T.amber, margin: "5px 0 0" }}>
              {pendingDelIds.size} row{pendingDelIds.size > 1 ? "s" : ""} will be removed when you save.
            </p>
          )}
        </div>
      )}

      {/* Add-row form */}
      <div>
        <div style={{ fontSize: T.fsXxs, fontWeight: T.fwSemibold, color: T.mid, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>
          Add year
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr 1fr auto", gap: 6, alignItems: "end" }}>
          {[
            { key: "year",  label: "Year",            testId: "yc-year-input" },
            { key: "rent",  label: YC_RENT_LABEL,     testId: null },
            { key: "opex",  label: "OpEx",            testId: null },
            { key: "capex", label: "Capex",           testId: null },
          ].map(({ key, label, testId }) => (
            <div key={key}>
              <label style={{ fontSize: T.fsXxs, color: T.mid, display: "block", marginBottom: 2 }} title={label}>{label}</label>
              <input
                type="number"
                data-testid={testId ?? undefined}
                value={newRowDraft[key]}
                placeholder=""
                onChange={(e) => onNewRowChange(key, e.target.value)}
                disabled={submitting}
                style={{ ...INPUT_BASE, width: "100%" }}
              />
            </div>
          ))}
          <button
            type="button"
            onClick={onAddRow}
            disabled={submitting || !newRowDraft.year}
            style={{
              background: !newRowDraft.year ? T.lightGray : T.blue,
              color: !newRowDraft.year ? T.mid : T.white,
              border: "none", padding: "6px 14px", borderRadius: 4,
              fontSize: T.fsSm, fontWeight: T.fwSemibold,
              cursor: submitting || !newRowDraft.year ? "not-allowed" : "pointer",
            }}
          >Add</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

const EMPTY_NEW_ROW = { year: "", rent: "", opex: "", capex: "" };

export function EditLeaseModal({ isOpen, onClose, lease, onSaved, canWrite = false, isAdmin = false, onDeleted = () => {}, initialField = null }) {
  const [areaAsOf] = useState(() => new Date());
  const [areaDrafts, setAreaDrafts] = useState({});
  const [draft, setDraft]                 = useState(null);
  const [pendingDelIds, setPendingDelIds] = useState(new Set());
  const [pendingNewRows, setPendingNewRows] = useState([]);
  const [newRowDraft, setNewRowDraft]     = useState(EMPTY_NEW_ROW);
  // ycEdits: Map<String(id), {rent?, opex?, capex?}> — in-progress edits for
  // persisted year-cost rows. Populated by handleYcEdit; PATCHed on save.
  const [ycEdits, setYcEdits]             = useState(new Map());
  const [submitting, setSubmitting]       = useState(false);
  const [formError, setFormError]         = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // LEASE-DELETE-MANAGE-AFFORDANCE
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [voidOpen, setVoidOpen]           = useState(false);
  const [voidReason, setVoidReason]       = useState("");
  const [deleteError, setDeleteError]     = useState(null);
  const [deleting, setDeleting]           = useState(false);
  const [expanded, setExpanded] = useState({
    summary: true, financial: true, aiSummary: true,
    yearCosts: false, criticalDates: false, options: false, restrictions: false,
  });
  const bodyRef = useRef(null);

  // ── Initialise draft on every open ───────────────────────────────────────
  useEffect(() => {
    if (isOpen && lease) {
      setDraft({
        aiSummary:              lease.aiSummary ?? null,
        status:                 dbStatusToEditBucket(lease.status, lease.type, lease.leaseStatus),
        type:                   lease.type ?? null,
        size:                   lease.size ?? null,
        vendor:                 lease.vendor ?? null,
        lessee:                 lease.lessee ?? null,
        startDate:              lease.startDate ?? null,
        endDate:                lease.endDate ?? null,
        moveInDate:             lease.moveInDate ?? null,
        moveOutDate:            lease.moveOutDate ?? null,
        totalCost:              lease.totalCost ?? null,
        currency:               lease.currency ?? null,
        rentBasis:              lease.rentBasis ?? null,
        freeRentMonths:         lease.freeRentMonths ?? null,
        escalationType:         lease.escalationType ?? null,
        opexStructure:          lease.opexStructure ?? null,
        breakDate:              lease.breakDate ?? null,
        renewalNoticeMonths:    lease.renewalNoticeMonths ?? null,
        renewalCount:           lease.renewalCount ?? null,
        rofrRofoNotes:          lease.rofrRofoNotes ?? null,
        expansionOptionNotes:   lease.expansionOptionNotes ?? null,
        // LEASE-FIELDS-ADD-DROP: holdoverPercent removed (column dropped in 0216)
        securityDepositAmount:  lease.securityDepositAmount ?? null,
        permittedUse:           lease.permittedUse ?? null,
        subletConsentRequired:  lease.subletConsentRequired ?? null,
        sqftMeasurementStandard: lease.sqftMeasurementStandard ?? null,
        subletRecaptureRight:   lease.subletRecaptureRight ?? null,
        restorationObligations: lease.restorationObligations ?? null,
        subletProfitSharePercent: lease.subletProfitSharePercent ?? null,
        baseYear:               lease.baseYear ?? null,
        baseYearOpex:           lease.baseYearOpex ?? null,
        baseYearTax:            lease.baseYearTax ?? null,
        opexCapPercent:         lease.opexCapPercent ?? null,
        noticeAddress:          lease.noticeAddress ?? null,
      });
      setPendingDelIds(new Set());
      setPendingNewRows([]);
      setNewRowDraft(EMPTY_NEW_ROW);
      setYcEdits(new Map());
      setAreaDrafts({});
      setFormError(null);
      setConfirmDiscard(false);

      // Pre-expand the target section when opened from a "+ add" affordance.
      if (initialField) {
        const targetSection = FIELD_TO_SECTION[initialField];
        if (targetSection && !ALWAYS_EXPANDED_SECTIONS.has(targetSection)) {
          setExpanded((e) => ({ ...e, [targetSection]: true }));
        }
      }
    }
    if (!isOpen) setConfirmDiscard(false);
  }, [isOpen, lease?.id, initialField]);

  // Focus the first input in the target section (or the first input overall).
  // When initialField is set, scroll the section into view and focus its first
  // focusable element so editors land directly on the relevant field.
  useEffect(() => {
    if (!isOpen) return undefined;
    const raf = requestAnimationFrame(() => {
      if (initialField) {
        const targetSection = FIELD_TO_SECTION[initialField];
        if (targetSection) {
          const sectionEl = bodyRef.current?.querySelector(`[data-section="${targetSection}"]`);
          if (sectionEl) {
            sectionEl.scrollIntoView({ behavior: "smooth", block: "start" });
            sectionEl.querySelector("input,select,textarea")?.focus();
            return;
          }
        }
      }
      // Fallback: focus the first focusable element in the modal body.
      bodyRef.current?.querySelector("input,select,textarea")?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen, initialField]);

  // ── Dirty detection ───────────────────────────────────────────────────────
  const isDirty = useMemo(() => {
    if (!draft || !lease) return false;
    if (pendingDelIds.size > 0 || pendingNewRows.length > 0 || ycEdits.size > 0 || Object.keys(areaDrafts).length > 0) return true;
    // `status` in draft is a derived display-bucket; compare against the initial
    // derived bucket (not the raw DB value) to avoid false dirty signals.
    const initialBucket = dbStatusToEditBucket(lease.status, lease.type, lease.leaseStatus);
    if (draft.status !== initialBucket) return true;
    return Object.keys(draft).some((k) => {
      if (k === "status") return false; // already checked above
      return !valuesEqual(draft[k], lease[k] ?? null);
    });
  }, [draft, lease, pendingDelIds, pendingNewRows, ycEdits, areaDrafts]);

  // ── Next-year suggestion — kept in sync with persisted + pending rows ──────
  const nextYearSuggestion = useMemo(() => {
    const allRows = [
      ...(Array.isArray(lease?.yearCosts) ? lease.yearCosts : []),
      ...pendingNewRows,
    ];
    return allRows.length > 0
      ? Math.max(...allRows.map((r) => Number(r.year))) + 1
      : new Date().getFullYear();
  }, [lease?.yearCosts, pendingNewRows]);

  // Pre-populate Year input with the suggestion whenever it changes
  useEffect(() => {
    setNewRowDraft((d) => ({ ...d, year: String(nextYearSuggestion) }));
  }, [nextYearSuggestion]);

  // ── Field change helper ───────────────────────────────────────────────────
  function setField(field, value) {
    setDraft((d) => (d ? { ...d, [field]: value } : d));
    if (formError) setFormError(null);
  }

  // ── Section toggle ────────────────────────────────────────────────────────
  function toggle(key) {
    setExpanded((e) => ({ ...e, [key]: !e[key] }));
  }

  // ── Close / discard guard ─────────────────────────────────────────────────
  function handleAttemptClose() {
    if (submitting) return;
    if (isDirty) { setConfirmDiscard(true); } else { onClose(); }
  }

  // ── Delete draft / Void archive ──────────────────────────────────────────
  const isDraftLease = lease?.status === "draft";

  async function handleConfirmDeleteDraft() {
    if (deleting || !lease) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteDraftLease(lease.locationId, lease.id, { before: lease });
      showToast("Lease deleted", { type: "success" });
      setConfirmDelete(false);
      onDeleted();
    } catch (err) {
      setDeleteError(err?.message || "Failed to delete draft lease");
    } finally {
      setDeleting(false);
    }
  }

  async function handleConfirmVoid() {
    if (deleting || !lease) return;
    if (!voidReason.trim()) {
      setDeleteError("A reason is required to archive this lease");
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await voidLease(lease.locationId, lease.id, voidReason, { before: lease });
      showToast("Lease archived", { type: "success" });
      setVoidOpen(false);
      onDeleted();
    } catch (err) {
      setDeleteError(err?.message || "Failed to archive lease");
    } finally {
      setDeleting(false);
    }
  }

  // ── Year-cost helpers ─────────────────────────────────────────────────────
  function handleDeleteExisting(id) {
    setPendingDelIds((s) => new Set([...s, id]));
  }
  function handleNewRowChange(field, value) {
    setNewRowDraft((d) => ({ ...d, [field]: value }));
  }
  /** Record an in-progress edit for a persisted year-cost row. */
  function handleYcEdit(id, field, value) {
    setYcEdits((m) => {
      const next = new Map(m);
      const cur  = next.get(id) ?? {};
      next.set(id, { ...cur, [field]: value });
      return next;
    });
    if (formError) setFormError(null);
  }
  function handleAddRow() {
    const year = parseInt(newRowDraft.year, 10);
    if (!year || isNaN(year)) return;
    setPendingNewRows((rows) => [
      ...rows,
      {
        year,
        rent:  newRowDraft.rent  ? parseFloat(newRowDraft.rent)  : null,
        opex:  newRowDraft.opex  ? parseFloat(newRowDraft.opex)  : null,
        capex: newRowDraft.capex ? parseFloat(newRowDraft.capex) : null,
        totalCost: null,
      },
    ]);
    setNewRowDraft(EMPTY_NEW_ROW);
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  async function handleSave() {
    if (submitting || !draft || !lease) return;
    const areaPatches = [];
    for (const [id, edits] of Object.entries(areaDrafts)) {
      const area = lease.areas?.find((row) => row.id === id);
      if (!area || !(canWrite || isAdmin)) continue;
      const patch = {};
      for (const [key, text] of Object.entries(edits)) {
        if (key === "rentRates") {
          const parsed = parseRentRateDrafts(text);
          if (parsed.error) { setFormError(`${area.name}: ${parsed.error}`); return; }
          const original = parseRentRateDrafts((area.rentRates || []).map(rateDraft)).rates;
          if (JSON.stringify(parsed.rates) !== JSON.stringify(original)) patch.rentRates = parsed.rates;
          continue;
        }
        // Clearing this editor is not an explicit request to erase stored footage.
        if (String(text ?? "").trim() === "") continue;
        const parsed = parseAreaFootage(text);
        if (parsed.error) { setFormError(`${area.name}: ${parsed.error}`); return; }
        if (!valuesEqual(parsed.value, area[key] ?? null)) patch[key] = parsed.value;
      }
      if (Object.keys(patch).length) areaPatches.push({ id, patch });
    }
    setSubmitting(true);
    setFormError(null);
    try {
      // Build minimal patch — only fields that changed.
      // `status` is stored in draft as a derived display-bucket (in_use / draft /
      // sublease_out / expired); compare against the initial derived bucket and
      // translate back to the DB column value before patching.
      const patch = {};
      for (const k of Object.keys(draft)) {
        if (k === "status") continue; // handled below
        if (!valuesEqual(draft[k], lease[k] ?? null)) patch[k] = draft[k];
      }
      const initialBucket = dbStatusToEditBucket(lease.status, lease.type, lease.leaseStatus);
      if (draft.status !== initialBucket) {
        patch.status = LEASE_BUCKET_TO_DB_STATUS[draft.status] ?? draft.status;
        // The explicit bucket is the source of truth for the user-visible
        // status. Sending only `status` would make Vacant-Held snap back to
        // In-Use on the next read.
        patch.leaseStatus = draft.status;
      }
      let serverLease = null;
      let wrote = false;
      const savedAreas = [...(Array.isArray(lease.areas) ? lease.areas : [])];
      for (const { id, patch: areaPatch } of areaPatches) {
        const saved = await updateLeaseArea(lease.locationId, lease.id, id, areaPatch);
        wrote = true;
        const index = savedAreas.findIndex((area) => area.id === id);
        savedAreas[index] = saved;
        // Retain server-minted segment IDs if a later area/lease write fails.
        // A retry must edit the committed segment, not insert it a second time.
        if (areaPatch.rentRates) setAreaDrafts((previous) => ({
          ...previous, [id]: { ...previous[id], rentRates: (saved.rentRates || []).map(rateDraft) },
        }));
      }
      if (Object.keys(patch).length > 0) {
        serverLease = await updateLease(lease.locationId, lease.id, patch, { before: lease });
        wrote = true;
      }
      // PATCH in-place edits to existing year-cost rows — writes to the same
      // lease_year_costs rows the rent schedule reads, so KPIs re-render from
      // the corrected values after the next refetch.
      // EDIT-LEASE-MODAL-PHASE2B: abatement is now included in the patch.
      const allExistingRows = Array.isArray(lease.yearCosts) ? lease.yearCosts : [];
      for (const [id, edits] of ycEdits.entries()) {
        const originalRow = allExistingRows.find((r) => String(r.id) === String(id));
        if (!originalRow) continue;
        const effRent      = "rent"      in edits ? edits.rent      : Number(originalRow.rent      ?? 0);
        const effOpex      = "opex"      in edits ? edits.opex      : Number(originalRow.opex      ?? 0);
        const effCapex     = "capex"     in edits ? edits.capex     : Number(originalRow.capex     ?? 0);
        const effAbatement = "abatement" in edits ? edits.abatement : Number(originalRow.abatement ?? 0);
        if (
          valuesEqual(effRent,      originalRow.rent) &&
          valuesEqual(effOpex,      originalRow.opex) &&
          valuesEqual(effCapex,     originalRow.capex) &&
          valuesEqual(effAbatement, originalRow.abatement ?? 0)
        ) continue; // nothing actually changed
        await updateYearCost(
          lease.locationId, lease.id, id,
          { rent: effRent, opex: effOpex, capex: effCapex, abatement: effAbatement },
          { before: originalRow }
        );
        wrote = true;
      }
      for (const id of pendingDelIds) {
        const beforeRow = allExistingRows.find((r) => String(r.id) === String(id)) ?? null;
        await deleteYearCost(lease.locationId, lease.id, id, { before: beforeRow });
        wrote = true;
      }
      for (const row of pendingNewRows) {
        await addYearCost(lease.locationId, lease.id, row);
        wrote = true;
      }
      // FLAG-STALE-CDS: forward staleCriticalDateIds (array of IDs) and count
      // from the server response so the caller can highlight the specific rows.
      const meta = {
        staleCriticalDateIds:    serverLease?.staleCriticalDateIds    ?? [],
        staleCriticalDatesCount: serverLease?.staleCriticalDatesCount ?? 0,
      };
      const optimisticLease = { ...lease, ...draft, areas: savedAreas };
      if (draft.status !== initialBucket) {
        optimisticLease.status = LEASE_BUCKET_TO_DB_STATUS[draft.status] ?? draft.status;
        optimisticLease.leaseStatus = draft.status;
      }
      // Prefer the server's canonical response. The optimistic fallback is
      // only for callers/tests whose write response does not include a row.
      if (wrote) showToast("Lease saved", { type: "success" });
      onSaved?.(serverLease?.id ? { ...serverLease, areas: savedAreas } : optimisticLease, meta);
      onClose();
    } catch (err) {
      log.error("EditLeaseModal save failed", { leaseId: lease?.id, err: String(err) });
      setFormError(err?.message || "Save failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Boolean ↔ select helpers ──────────────────────────────────────────────
  const toSel  = (v) => (v == null ? null : (v ? "Yes" : "No"));
  const fromSel = (v) => (v == null || v === "" ? null : v === "Yes");

  if (!isOpen || !lease || !draft) return null;

  const ycRows = [
    ...(Array.isArray(lease.yearCosts) ? lease.yearCosts : []),
    ...pendingNewRows.map((r) => ({ ...r, id: `pending-${r.year}`, _pending: true })),
  ];
  const ccy    = draft.currency || lease.currency || "USD";
  const monthlyDerivation = lease.computed?.monthlyRentDerivation;
  const monthlyAmount = monthlyDerivation ? monthlyDerivation.amount : lease.computed?.monthlyRent;
  const monthlyCurrency = monthlyDerivation?.currency || lease.currency || "USD";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleAttemptClose}
      ariaLabelledBy={TITLE_ID}
      testId="edit-lease-modal"
      overlayTestId="edit-lease-modal-overlay"
      maxWidth={680}
      disableBackdropClose
      title="Edit lease data"
      subTitle={lease?.id}
      closeTestId="edit-lease-modal-close-x"
    >

      {/* ── Body ─────────────────────────────────────────────────── */}
      <div ref={bodyRef} data-testid="edit-lease-modal-body" style={{ overflowY: "auto", flex: 1, padding: "12px 20px" }}>
        {formError && (
          <div role="alert" data-testid="edit-lease-modal-form-error" style={{
            marginBottom: 12, padding: "8px 12px",
            background: T.redSoft, border: `1px solid ${T.redLight}`,
            borderRadius: 6, color: T.red, fontSize: T.fsSm, fontWeight: T.fwSemibold,
          }}>
            {formError}
          </div>
        )}

        {/* ─ 0. LEASE NARRATIVE (expanded, collapsible) ──────── */}
        <div data-section="aiSummary" style={{ marginBottom: 16 }}>
          <SectionBar
            title="Lease summary"
            collapsible
            expanded={expanded.aiSummary}
            onToggle={() => toggle("aiSummary")}
          />
          {expanded.aiSummary && (
            <div style={{ paddingTop: 8 }}>
              <textarea
                data-testid="lease-summary-textarea"
                value={draft?.aiSummary ?? ""}
                onChange={(e) => setField("aiSummary", e.target.value)}
                placeholder="Plain-English summary of this lease."
                maxLength={3000}
                rows={6}
                disabled={submitting}
                style={{
                  ...INPUT_BASE,
                  resize: "vertical",
                  opacity: submitting ? 0.6 : 1,
                }}
              />
              <div style={{ fontSize: T.fsXs, color: T.light, textAlign: "right", marginTop: 3 }}>
                {(draft?.aiSummary ?? "").length}/3000
              </div>
            </div>
          )}
        </div>

        {/* ─ 1. LEASE DETAILS (expanded) ──────────────────────── */}
        <div data-section="leaseDetails" style={{ marginBottom: 16 }}>
          <SectionBar title="Lease details" />
          <FRow label="Status">
            <SelInput value={draft.status} onChange={(v) => setField("status", v)}
              options={LEASE_STATUS_OPTIONS} disabled={submitting} ariaLabel="Status" />
          </FRow>
          <FRow label="Type">
            <SelInput value={draft.type} onChange={(v) => setField("type", v)}
              options={LEASE_TYPE_OPTIONS} disabled={submitting} />
          </FRow>
          <FRow label="Size (sf)">
            <NumInput value={draft.size} onChange={(v) => setField("size", v)} disabled={submitting} min={0} />
            <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 4 }}>
              Recorded parent lease size. Edit individual gross and usable footage in Areas below; these values are separate.
            </div>
          </FRow>
          {/* Child footage uses its own audited boundary; dates/lifecycle stay read-only. */}
          {Array.isArray(lease.areas) && lease.areas.length > 0 && (
            <div data-section="areas">
              <LeaseAreasTable areas={lease.areas} asOf={areaAsOf} />
              {(canWrite || isAdmin) && <LeaseAreaEditor areas={lease.areas} drafts={areaDrafts} currency={lease.currency}
                asOf={areaAsOf}
                disabled={submitting} onChange={(id, key, value) => {
                  setAreaDrafts((previous) => ({ ...previous, [id]: { ...previous[id], [key]: value } }));
                  setFormError(null);
                }} />}
            </div>
          )}
          <FRow label="Lessor">
            <TxtInput value={draft.vendor} onChange={(v) => setField("vendor", v)} disabled={submitting} />
          </FRow>
          <FRow label="Lessee">
            <TxtInput value={draft.lessee} onChange={(v) => setField("lessee", v)} disabled={submitting} />
          </FRow>
          <FRow label="Commencement">
            <DtInput value={draft.startDate} onChange={(v) => setField("startDate", v)} disabled={submitting} />
          </FRow>
          <FRow label="End date">
            <DtInput value={draft.endDate} onChange={(v) => setField("endDate", v)} disabled={submitting} />
          </FRow>
          {/* LSE-MOVE-IN-OUT-DATES: physical occupancy dates — distinct from contractual dates above */}
          <FRow label="Move-in date">
            <DtInput value={draft.moveInDate} onChange={(v) => setField("moveInDate", v)} disabled={submitting} />
          </FRow>
          <FRow label="Move-out date">
            <DtInput value={draft.moveOutDate} onChange={(v) => setField("moveOutDate", v)} disabled={submitting} />
          </FRow>
          <RORow label="Term length"
            value={lease.computed?.termLengthMonths != null ? `${lease.computed.termLengthMonths} months` : null} />
          <RORow label="Lease ID" value={lease.id} />
        </div>

        {/* ─ 2. FINANCIAL TERMS (expanded) ───────────────────── */}
        <div data-section="financial" style={{ marginBottom: 16 }}>
          <SectionBar title="Financial terms" />
          <FRow label="Total cost (lease term)">
            <NumInput value={draft.totalCost} onChange={(v) => setField("totalCost", v)} disabled={submitting} min={0} />
          </FRow>
          <FRow label="Currency">
            <TxtInput value={draft.currency} onChange={(v) => setField("currency", v)} placeholder="USD" disabled={submitting} />
          </FRow>
          {isAdmin && (
            <FRow label="Rent basis">
              <SelInput
                value={draft.rentBasis ?? "not_recorded"}
                onChange={(v) => setField("rentBasis", v)}
                ariaLabel="Rent basis"
                options={[
                  { value: "fixed", label: "Fixed rent" },
                  { value: "zero_sublease", label: "Confirmed zero-rent sublease" },
                  { value: "rent_free", label: "Rent-free" },
                  { value: "per_lease_terms", label: "Per lease terms" },
                  { value: "not_recorded", label: "Not recorded" },
                ]}
                disabled={submitting}
              />
            </FRow>
          )}
          <FRow label="Free rent (months)">
            <NumInput value={draft.freeRentMonths} onChange={(v) => setField("freeRentMonths", v)} disabled={submitting} min={0} />
          </FRow>
          <FRow label="Escalation">
            <SelInput value={draft.escalationType} onChange={(v) => setField("escalationType", v)}
              options={["Fixed", "CPI", "None"]} disabled={submitting} />
          </FRow>
          <FRow label="OpEx structure">
            <SelInput value={draft.opexStructure} onChange={(v) => setField("opexStructure", v)}
              options={["NNN", "Modified Gross", "Full Service Gross", "Gross"]} disabled={submitting} />
          </FRow>
          <RORow label="Annual rent (derived)"
            value={lease.computed?.annualRent != null
              ? `${ccy} ${Number(lease.computed.annualRent).toLocaleString()}`
              : null} />
          <RORow label={monthlyDerivation ? "Monthly rent" : "Monthly rent (derived)"}
            value={monthlyAmount != null
              ? <span data-testid="edit-lease-monthly-rent">
                  {`${monthlyCurrency} ${Number(monthlyAmount).toLocaleString()}`}
                  <MonthlyRentBasis derivation={monthlyDerivation} />
                </span>
              : null} />
          {monthlyDerivation?.approximate && monthlyAmount != null && <div
            data-testid="edit-lease-monthly-installment-note"
            style={{ fontSize: T.fsXs, color: T.mid, marginTop: 6 }}>
            {EVEN_INSTALLMENT_NOTE}
          </div>}
        </div>

        {/* ─ 3. PER-YEAR COSTS (collapsed) ───────────────────── */}
        <div data-section="yearCosts" style={{ marginBottom: 16 }}>
          <SectionBar title="Per-year costs" collapsible expanded={expanded.yearCosts} onToggle={() => toggle("yearCosts")} />
          {expanded.yearCosts && (
            <YearCostsTable
              rows={ycRows}
              pendingDelIds={pendingDelIds}
              newRowDraft={newRowDraft}
              onDeleteExisting={handleDeleteExisting}
              onAddRow={handleAddRow}
              onNewRowChange={handleNewRowChange}
              ycEdits={ycEdits}
              onYcEdit={handleYcEdit}
              submitting={submitting}
            />
          )}
        </div>

        {/* ─ 4. CRITICAL DATES (collapsed) ───────────────────── */}
        {/*
          DECISION: critical_dates table fields (expiry, escalation_dates,
          abatement_start, abatement_end) are candidates for future read-only
          display in this section — not built yet.
          breakDate and renewalNoticeMonths are editable here because they are
          stored directly on the lease row and are the primary actionable dates.
          nextNoticeDeadline and noticePeriodText are computed/imported; shown
          read-only only.
        */}
        <div data-section="criticalDates" style={{ marginBottom: 16 }}>
          <SectionBar title="Critical dates" collapsible expanded={expanded.criticalDates} onToggle={() => toggle("criticalDates")} />
          {expanded.criticalDates && (
            <>
              <FRow label="Break option date">
                <DtInput value={draft.breakDate} onChange={(v) => setField("breakDate", v)} disabled={submitting} />
              </FRow>
              <FRow label="Renewal notice (months)">
                <NumInput value={draft.renewalNoticeMonths} onChange={(v) => setField("renewalNoticeMonths", v)} disabled={submitting} min={0} />
              </FRow>
              <RORow label="Next notice deadline"
                value={lease.nextNoticeDeadline ? String(lease.nextNoticeDeadline).slice(0, 10) : null} />
              <RORow label="Notice period text" value={lease.noticePeriodText} />
            </>
          )}
        </div>

        {/* ─ 5. OPTIONS (collapsed) ───────────────────────────── */}
        <div data-section="options" style={{ marginBottom: 16 }}>
          <SectionBar title="Options" collapsible expanded={expanded.options} onToggle={() => toggle("options")} />
          {expanded.options && (
            <>
              <FRow label="Renewal options">
                <NumInput value={draft.renewalCount} onChange={(v) => setField("renewalCount", v)} disabled={submitting} min={0} />
              </FRow>
              <RORow label="Term per renewal"
                value={lease.renewalTermYears != null ? `${lease.renewalTermYears} years` : null} />
              <FRow label="ROFR / ROFO notes">
                <TxtInput value={draft.rofrRofoNotes} onChange={(v) => setField("rofrRofoNotes", v)} disabled={submitting} />
              </FRow>
              <FRow label="Expansion option notes">
                <TxtInput value={draft.expansionOptionNotes} onChange={(v) => setField("expansionOptionNotes", v)} disabled={submitting} />
              </FRow>
              {/* LEASE-FIELDS-ADD-DROP: Holdover % field removed (column dropped in 0216) */}
              <FRow label="Security deposit amount">
                <NumInput value={draft.securityDepositAmount} onChange={(v) => setField("securityDepositAmount", v)} disabled={submitting} min={0} />
              </FRow>
            </>
          )}
        </div>

        {/* ─ 6. RESTRICTIONS & CONCESSIONS (collapsed) ─────── */}
        <div data-section="restrictions" style={{ marginBottom: 8 }}>
          <SectionBar title="Restrictions & concessions" collapsible expanded={expanded.restrictions} onToggle={() => toggle("restrictions")} />
          {expanded.restrictions && (
            <>
              <FRow label="Permitted use">
                <TxtInput value={draft.permittedUse} onChange={(v) => setField("permittedUse", v)} disabled={submitting} />
              </FRow>
              <FRow label="Sublet consent required">
                <SelInput
                  value={toSel(draft.subletConsentRequired)}
                  onChange={(v) => setField("subletConsentRequired", fromSel(v))}
                  options={["Yes", "No"]} disabled={submitting}
                />
              </FRow>
              <FRow label="Sqft measurement std">
                <TxtInput value={draft.sqftMeasurementStandard} onChange={(v) => setField("sqftMeasurementStandard", v)} disabled={submitting} />
              </FRow>
              <FRow label="Recapture right">
                <SelInput
                  value={toSel(draft.subletRecaptureRight)}
                  onChange={(v) => setField("subletRecaptureRight", fromSel(v))}
                  options={["Yes", "No"]} disabled={submitting}
                />
              </FRow>
              <FRow label="Restoration obligations">
                <TxtInput value={draft.restorationObligations} onChange={(v) => setField("restorationObligations", v)} disabled={submitting} />
              </FRow>
              <FRow label="Profit share %">
                <NumInput value={draft.subletProfitSharePercent} onChange={(v) => setField("subletProfitSharePercent", v)} disabled={submitting} min={0} />
              </FRow>
              <FRow label="Base year">
                <NumInput value={draft.baseYear} onChange={(v) => setField("baseYear", v)} disabled={submitting} />
              </FRow>
              <FRow label="Base year OpEx">
                <NumInput value={draft.baseYearOpex} onChange={(v) => setField("baseYearOpex", v)} disabled={submitting} min={0} />
              </FRow>
              <FRow label="Base year tax">
                <NumInput value={draft.baseYearTax} onChange={(v) => setField("baseYearTax", v)} disabled={submitting} min={0} />
              </FRow>
              <FRow label="OpEx cap %">
                <NumInput value={draft.opexCapPercent} onChange={(v) => setField("opexCapPercent", v)} disabled={submitting} min={0} />
              </FRow>
              <FRow label="Notice address">
                <TxtInput value={draft.noticeAddress} onChange={(v) => setField("noticeAddress", v)} disabled={submitting} />
              </FRow>
            </>
          )}
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "flex-end",
        gap: 8, padding: "12px 20px", borderTop: `1px solid ${T.border}`, flexShrink: 0,
      }}>
        {/* LEASE-DELETE-MANAGE-AFFORDANCE: draft → hard delete (editor/admin);
            non-draft → admin-only void/archive. Pushed to the left via
            marginRight: auto so Cancel/Save stay right-aligned. */}
        {isDraftLease && canWrite && (
          <button
            type="button"
            onClick={() => { setDeleteError(null); setConfirmDelete(true); }}
            disabled={submitting}
            data-testid="edit-lease-modal-delete-draft"
            style={{
              background: "transparent", color: T.red, border: `1px solid ${T.red}`,
              padding: "7px 14px", borderRadius: 6, fontSize: T.fsSm5, fontWeight: T.fwSemibold,
              cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1,
              marginRight: "auto",
            }}
          >Delete</button>
        )}
        {!isDraftLease && isAdmin && (
          <button
            type="button"
            onClick={() => { setDeleteError(null); setVoidReason(""); setVoidOpen(true); }}
            disabled={submitting}
            data-testid="edit-lease-modal-void"
            style={{
              background: "transparent", color: T.red, border: `1px solid ${T.red}`,
              padding: "7px 14px", borderRadius: 6, fontSize: T.fsSm5, fontWeight: T.fwSemibold,
              cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1,
              marginRight: "auto",
            }}
          >Archive</button>
        )}
        <button
          type="button"
          onClick={handleAttemptClose}
          disabled={submitting}
          data-testid="edit-lease-modal-cancel"
          style={{
            background: "transparent", color: T.mid, border: `1px solid ${T.border}`,
            padding: "7px 14px", borderRadius: 6, fontSize: T.fsSm5, fontWeight: T.fwSemibold,
            cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1,
          }}
        >Cancel</button>
        <button
          type="button"
          onClick={handleSave}
          disabled={submitting}
          data-testid="edit-lease-modal-save"
          style={{
            background: submitting ? T.blue : isDirty ? T.blue : T.lightGray,
            color: submitting ? T.white : isDirty ? T.white : T.mid,
            border: "none",
            padding: "7px 16px", borderRadius: 6, fontSize: T.fsSm5, fontWeight: T.fwBold,
            cursor: submitting ? "not-allowed" : isDirty ? "pointer" : "default",
            opacity: submitting ? 0.7 : 1,
          }}
        >{submitting ? "Saving…" : "Save changes"}</button>
      </div>

      {/* ── Discard confirm overlay ───────────────────────────────── */}
      {confirmDiscard && (
        <div
          data-testid="edit-lease-discard-overlay"
          style={{
            position: "absolute", inset: 0, background: "rgba(255,255,255,0.93)",
            display: "flex", alignItems: "center", justifyContent: "center",
            borderRadius: 10, zIndex: 1,
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="edit-lease-discard-title"
            data-testid="edit-lease-discard-confirm"
            style={{
              background: T.white, borderRadius: 8,
              boxShadow: T.shadowEditModal,
              padding: 24, width: "100%", maxWidth: 360, margin: 20,
            }}
          >
            <h4 id="edit-lease-discard-title" style={{ margin: "0 0 8px", fontSize: T.fsMd, fontWeight: T.fwBold, color: T.dark }}>
              Discard unsaved changes?
            </h4>
            <p style={{ margin: "0 0 20px", fontSize: T.fsBase, color: T.mid }}>
              Your edits to {lease.id} will be lost.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                onClick={() => setConfirmDiscard(false)}
                data-testid="edit-lease-discard-cancel"
                style={{ padding: "6px 14px", fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark, background: T.white, border: `1px solid ${T.border}`, borderRadius: 6, cursor: "pointer" }}
              >Keep editing</button>
              <button
                type="button"
                onClick={() => { setConfirmDiscard(false); onClose(); }}
                data-testid="edit-lease-discard-confirm-btn"
                style={{ padding: "6px 14px", fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.white, background: T.red, border: "none", borderRadius: 6, cursor: "pointer" }}
              >Discard</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete draft confirm overlay ────────────────────────────── */}
      {confirmDelete && (
        <div
          data-testid="edit-lease-delete-overlay"
          style={{
            position: "absolute", inset: 0, background: "rgba(255,255,255,0.93)",
            display: "flex", alignItems: "center", justifyContent: "center",
            borderRadius: 10, zIndex: 1,
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="edit-lease-delete-title"
            data-testid="edit-lease-delete-confirm"
            style={{
              background: T.white, borderRadius: 8,
              boxShadow: T.shadowEditModal,
              padding: 24, width: "100%", maxWidth: 380, margin: 20,
            }}
          >
            <h4 id="edit-lease-delete-title" style={{ margin: "0 0 8px", fontSize: T.fsMd, fontWeight: T.fwBold, color: T.dark }}>
              Delete this draft lease?
            </h4>
            <p style={{ margin: "0 0 16px", fontSize: T.fsBase, color: T.mid }}>
              {lease.id} will be permanently deleted. This cannot be undone.
            </p>
            {deleteError && (
              <p data-testid="edit-lease-delete-error" style={{ margin: "0 0 16px", fontSize: T.fsSm5, color: T.red }}>
                {deleteError}
              </p>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                data-testid="edit-lease-delete-cancel"
                style={{ padding: "6px 14px", fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark, background: T.white, border: `1px solid ${T.border}`, borderRadius: 6, cursor: deleting ? "not-allowed" : "pointer" }}
              >Keep draft</button>
              <button
                type="button"
                onClick={handleConfirmDeleteDraft}
                disabled={deleting}
                data-testid="edit-lease-delete-confirm-btn"
                style={{ padding: "6px 14px", fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.white, background: T.red, border: "none", borderRadius: 6, cursor: deleting ? "not-allowed" : "pointer", opacity: deleting ? 0.7 : 1 }}
              >{deleting ? "Deleting…" : "Delete"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Void / archive confirm overlay (admin only, non-draft) ───── */}
      {voidOpen && (
        <div
          data-testid="edit-lease-void-overlay"
          style={{
            position: "absolute", inset: 0, background: "rgba(255,255,255,0.93)",
            display: "flex", alignItems: "center", justifyContent: "center",
            borderRadius: 10, zIndex: 1,
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="edit-lease-void-title"
            data-testid="edit-lease-void-confirm"
            style={{
              background: T.white, borderRadius: 8,
              boxShadow: T.shadowEditModal,
              padding: 24, width: "100%", maxWidth: 420, margin: 20,
            }}
          >
            <h4 id="edit-lease-void-title" style={{ margin: "0 0 8px", fontSize: T.fsMd, fontWeight: T.fwBold, color: T.dark }}>
              Archive this lease?
            </h4>
            <p style={{ margin: "0 0 12px", fontSize: T.fsBase, color: T.mid }}>
              {lease.id} moves to Recently Deleted and is removed from active views. You can restore it for 30 days.
            </p>
            <label htmlFor="edit-lease-void-reason" style={{ display: "block", fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 4 }}>
              Reason (required)
            </label>
            <textarea
              id="edit-lease-void-reason"
              data-testid="edit-lease-void-reason"
              value={voidReason}
              onChange={(e) => { setVoidReason(e.target.value); if (deleteError) setDeleteError(null); }}
              disabled={deleting}
              rows={3}
              style={{
                width: "100%", boxSizing: "border-box", padding: "8px 10px", marginBottom: 16,
                fontSize: T.fsSm5, fontFamily: "inherit", border: `1px solid ${T.border}`, borderRadius: 6, resize: "vertical",
              }}
            />
            {deleteError && (
              <p data-testid="edit-lease-void-error" style={{ margin: "0 0 16px", fontSize: T.fsSm5, color: T.red }}>
                {deleteError}
              </p>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                onClick={() => setVoidOpen(false)}
                disabled={deleting}
                data-testid="edit-lease-void-cancel"
                style={{ padding: "6px 14px", fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark, background: T.white, border: `1px solid ${T.border}`, borderRadius: 6, cursor: deleting ? "not-allowed" : "pointer" }}
              >Cancel</button>
              <button
                type="button"
                onClick={handleConfirmVoid}
                disabled={deleting || !voidReason.trim()}
                data-testid="edit-lease-void-confirm-btn"
                style={{
                  padding: "6px 14px", fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.white,
                  background: T.red, border: "none", borderRadius: 6,
                  cursor: deleting || !voidReason.trim() ? "not-allowed" : "pointer",
                  opacity: deleting || !voidReason.trim() ? 0.6 : 1,
                }}
              >{deleting ? "Archiving…" : "Archive"}</button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
